import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BellRinging, X, CheckCircle, Lightning, Calendar, Heart } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  notificationSupported,
  requestNotificationPermission,
  showLocalNotification,
} from "@/lib/pwa";
import { requestPushAndRegister } from "@/lib/onesignal";

const KEY = "fidelis_notif_prompt_dismissed_at";

const shouldAutoOpen = () => {
  if (!notificationSupported()) return false;
  if (Notification.permission === "granted" || Notification.permission === "denied") return false;
  const dismissed = Number(localStorage.getItem(KEY) || 0);
  if (!dismissed) return true;
  return Date.now() - dismissed > 1000 * 60 * 60 * 24; // try again after 24h
};

export const NotificationPrompt = () => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (shouldAutoOpen()) {
      const t = setTimeout(() => setOpen(true), 1800);
      return () => clearTimeout(t);
    }
  }, []);

  const dismiss = () => {
    localStorage.setItem(KEY, String(Date.now()));
    setOpen(false);
  };

  const enable = async () => {
    setBusy(true);
    const r = await requestNotificationPermission();
    if (r === "granted") {
      toast.success("Notificações ativadas!");
      showLocalNotification("Zeferius Pay", "Você receberá lembretes automáticos por aqui.");
      // Register the OneSignal player id with the backend
      await requestPushAndRegister();
      setOpen(false);
    } else if (r === "denied") {
      toast.error("Permissão negada. Você pode ativar depois nas configurações do navegador.");
      dismiss();
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && dismiss()}>
      <DialogContent className="max-w-md" data-testid="notif-prompt-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BellRinging size={22} weight="duotone" className="text-blue-600"/>
            Ative seus lembretes
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-zinc-600">
            Para você nunca esquecer um vencimento e <strong>manter seu desconto de fidelidade</strong>, ative as notificações no seu dispositivo.
          </p>

          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-700 grid place-items-center flex-shrink-0">
                <Calendar size={16} weight="fill"/>
              </div>
              <div>
                <div className="font-medium text-zinc-900">Lembretes inteligentes</div>
                <div className="text-zinc-600">Avisamos 7, 3 e 2 dias antes do vencimento — sem stress.</div>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center flex-shrink-0">
                <Lightning size={16} weight="fill"/>
              </div>
              <div>
                <div className="font-medium text-zinc-900">Pagamentos confirmados</div>
                <div className="text-zinc-600">Receba aviso assim que seu pagamento for aprovado.</div>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-rose-50 text-rose-700 grid place-items-center flex-shrink-0">
                <Heart size={16} weight="fill"/>
              </div>
              <div>
                <div className="font-medium text-zinc-900">Promoções exclusivas</div>
                <div className="text-zinc-600">Descontos e benefícios extras direto no seu celular.</div>
              </div>
            </li>
          </ul>

          <div className="flex flex-col gap-2 pt-2">
            <Button
              onClick={enable}
              disabled={busy}
              data-testid="notif-prompt-enable"
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {busy ? "Solicitando..." : (<><CheckCircle weight="fill" className="mr-2" size={18}/> Ativar notificações</>)}
            </Button>
            <button
              onClick={dismiss}
              data-testid="notif-prompt-dismiss"
              className="text-xs text-zinc-500 hover:text-zinc-800 underline text-center"
            >
              Agora não, lembrar depois
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationPrompt;
